import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Plus, Edit, Trash2, Globe, Loader2, AlertCircle, X, Shield } from 'lucide-react';
import { toast } from 'sonner';
import {
  apiService,
  WebhookTemplateResponse,
  CreateWebhookTemplateRequest,
  UpdateWebhookTemplateRequest,
  WebhookTemplateHeaderType,
} from '../../services/api';

interface KeyValuePair {
  key: string;
  value: string;
  type: WebhookTemplateHeaderType;
}

export function WebhookTemplatesPage() {
  const [templates, setTemplates] = useState<WebhookTemplateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WebhookTemplateResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    method: 'POST' as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    headers: [] as KeyValuePair[],
    payload: '',
    customPrompts: [] as string[]
  });
  const [payloadError, setPayloadError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getWebhookTemplates();
      setTemplates(response || []);
    } catch (err) {
      console.error('Failed to load webhook templates:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = (templates || []).filter(template => {
    const matchesSearch = (template.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (template.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (template.url || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      method: 'POST',
      headers: [],
      payload: '',
      customPrompts: []
    });
    setPayloadError(null);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (template: WebhookTemplateResponse) => {
    setEditingTemplate(template);

    // Convert headers array to form key-value pairs
    const headerPairs: KeyValuePair[] = template.headers
      ? template.headers.map((h) => ({ key: h.key, value: h.value, type: h.type }))
      : [];

    setFormData({
      name: template.name,
      description: template.description || '',
      url: template.url || '',
      method: template.method,
      headers: headerPairs.length > 0 ? headerPairs : [],
      payload: template.payload ? JSON.stringify(template.payload, null, 2) : '',
      customPrompts: template.customPrompts || []
    });
    setPayloadError(null);
    setIsDialogOpen(true);
  };

  const validatePayload = (payloadString: string): { valid: boolean; parsed?: Record<string, unknown>; error?: string } => {
    if (!payloadString.trim()) {
      return { valid: true, parsed: undefined };
    }

    try {
      const parsed = JSON.parse(payloadString);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { valid: false, error: 'Payload muss ein JSON-Objekt sein' };
      }
      return { valid: true, parsed };
    } catch (err) {
      return { valid: false, error: 'Ungültiges JSON-Format' };
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name ist erforderlich');
      return;
    }

    // Validate payload
    const payloadValidation = validatePayload(formData.payload);
    if (!payloadValidation.valid) {
      setPayloadError(payloadValidation.error || 'Ungültiges Payload-Format');
      return;
    }

    try {
      setSaving(true);
      setPayloadError(null);

      // Build headers array with type
      const headersArray = formData.headers
        .filter(({ key }) => key.trim())
        .map(({ key, value, type }) => ({ key: key.trim(), value, type }));

      const requestData: CreateWebhookTemplateRequest | UpdateWebhookTemplateRequest = {
        name: formData.name,
        description: formData.description || undefined,
        url: formData.url || undefined,
        method: formData.method,
        headers: headersArray.length > 0 ? headersArray : undefined,
        payload: payloadValidation.parsed,
        customPrompts: formData.customPrompts.length > 0 ? formData.customPrompts : undefined
      };

      if (editingTemplate) {
        const updatedTemplate = await apiService.updateWebhookTemplate(editingTemplate.id, requestData);
        setTemplates(templates.map(t => t.id === editingTemplate.id ? updatedTemplate : t));
        toast.success('Template erfolgreich aktualisiert');
      } else {
        const newTemplate = await apiService.createWebhookTemplate(requestData as CreateWebhookTemplateRequest);
        setTemplates([...templates, newTemplate]);
        toast.success('Template erfolgreich erstellt');
      }

      setIsDialogOpen(false);
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const template = templates.find(t => t.id === id);
    if (!template) return;

    if (confirm(`Sind Sie sicher, dass Sie das Template "${template.name}" löschen möchten?`)) {
      try {
        await apiService.deleteWebhookTemplate(id);
        setTemplates(templates.filter(t => t.id !== id));
        toast.success('Template erfolgreich gelöscht');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
        toast.error(`Fehler beim Löschen: ${errorMessage}`);
      }
    }
  };

  const addHeader = () => {
    setFormData({
      ...formData,
      headers: [...formData.headers, { key: '', value: '', type: 'regular' as WebhookTemplateHeaderType }]
    });
  };

  const removeHeader = (index: number) => {
    setFormData({
      ...formData,
      headers: formData.headers.filter((_, i) => i !== index)
    });
  };

  const updateHeader = (index: number, field: 'key' | 'value' | 'type', value: string) => {
    const newHeaders = [...formData.headers];
    if (field === 'type') {
      newHeaders[index] = { ...newHeaders[index], type: value as WebhookTemplateHeaderType };
    } else {
      newHeaders[index] = { ...newHeaders[index], [field]: value };
    }
    setFormData({ ...formData, headers: newHeaders });
  };

  const addCustomPrompt = () => {
    setFormData({
      ...formData,
      customPrompts: [...formData.customPrompts, '']
    });
  };

  const removeCustomPrompt = (index: number) => {
    setFormData({
      ...formData,
      customPrompts: formData.customPrompts.filter((_, i) => i !== index)
    });
  };

  const updateCustomPrompt = (index: number, value: string) => {
    const newPrompts = [...formData.customPrompts];
    newPrompts[index] = value;
    setFormData({ ...formData, customPrompts: newPrompts });
  };

  const getMethodColor = (method: string): string => {
    switch (method) {
      case 'GET':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'POST':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PUT':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'PATCH':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'DELETE':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhook Templates</h1>
          <p className="text-gray-600">Verwaltung wiederverwendbarer Webhook-Konfigurationen</p>
        </div>
        <Button onClick={handleCreate} disabled={loading}>
          <Plus className="w-4 h-4 mr-2" />
          Neues Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Übersicht</CardTitle>
              <CardDescription>{templates?.length || 0} Templates konfiguriert</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Templates suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Templates laden...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="flex items-center justify-center mb-4 text-red-600">
                <AlertCircle className="w-6 h-6 mr-2" />
                <span>{error}</span>
              </div>
              <Button onClick={loadTemplates} variant="outline">
                Erneut versuchen
              </Button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'Keine Templates gefunden' : 'Noch keine Templates konfiguriert'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTemplates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Globe className="w-5 h-5 text-blue-500" />
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <Badge variant="outline" className={getMethodColor(template.method)}>
                          {template.method}
                        </Badge>
                        {template.customPrompts && template.customPrompts.length > 0 && (
                          <Badge variant="outline" className="text-purple-700 border-purple-200">
                            {template.customPrompts.length} {template.customPrompts.length === 1 ? 'Prompt' : 'Prompts'}
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-gray-600 mb-2">{template.description}</p>
                      )}
                      {template.url && (
                        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                          <Globe className="w-3 h-3" />
                          <span className="font-mono truncate max-w-md">{template.url}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        {template.headers && template.headers.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span>{template.headers.length} Header</span>
                            {template.headers.some(h => h.type === 'auth') && (
                              <span className="inline-flex items-center text-amber-600">
                                <Shield className="w-3 h-3 mr-0.5" />
                                {template.headers.filter(h => h.type === 'auth').length} Auth
                              </span>
                            )}
                          </div>
                        )}
                        {template.payload && (
                          <div>Payload konfiguriert</div>
                        )}
                        <div>Erstellt: {new Date(template.createdAt).toLocaleDateString('de-DE')}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Template bearbeiten' : 'Neues Template erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie ein wiederverwendbares Webhook-Template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="z.B. Slack Benachrichtigung"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschreibung des Templates..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                placeholder="https://api.example.com/webhook"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="method">HTTP-Methode</Label>
              <Select
                value={formData.method}
                onValueChange={(value: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => setFormData({...formData, method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Headers</Label>
                <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                  <Plus className="w-3 h-3 mr-1" />
                  Header hinzufügen
                </Button>
              </div>
              {formData.headers.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Headers konfiguriert</p>
              ) : (
                <div className="space-y-2">
                  {formData.headers.map((header, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        placeholder="Key"
                        value={header.key}
                        onChange={(e) => updateHeader(index, 'key', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        className="flex-1"
                      />
                      <Select
                        value={header.type}
                        onValueChange={(value) => updateHeader(index, 'type', value)}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="regular">Regular</SelectItem>
                          <SelectItem value="auth">Auth</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHeader(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="payload">Payload (JSON)</Label>
              <Textarea
                id="payload"
                value={formData.payload}
                onChange={(e) => {
                  setFormData({...formData, payload: e.target.value});
                  setPayloadError(null);
                }}
                placeholder='{"message": "{{message}}", "user": "{{user}}"}'
                rows={6}
                className={`font-mono text-sm ${payloadError ? 'border-red-300' : ''}`}
              />
              {payloadError && (
                <div className="flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {payloadError}
                </div>
              )}
              <p className="text-xs text-gray-500">
                JSON-Objekt mit optionalen Variablen wie {`{{variable}}`}
              </p>
            </div>

            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Custom Prompts</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCustomPrompt}>
                  <Plus className="w-3 h-3 mr-1" />
                  Prompt hinzufügen
                </Button>
              </div>
              {formData.customPrompts.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Custom Prompts konfiguriert</p>
              ) : (
                <div className="space-y-3">
                  {formData.customPrompts.map((prompt, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Prompt {index + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomPrompt(index)}
                          className="text-red-600 hover:text-red-700 h-6"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <Textarea
                        value={prompt}
                        onChange={(e) => updateCustomPrompt(index, e.target.value)}
                        placeholder="Prompt-Text eingeben..."
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 border-t pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingTemplate ? 'Speichern...' : 'Erstellen...'}
                  </>
                ) : (
                  editingTemplate ? 'Speichern' : 'Erstellen'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
